import { useMemo, useState, useCallback, useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { debounce } from "lodash";

import { Box, Button } from "@mui/material";

import Api from "lib/api/httpRequestHelper";

import Filters from "./components/filters";
import SelectProductsTable from "./components/select-products-table";
import ModalComponent from "./components/modal";
import ProductsTable from "./components/products-table";

import UtilsFncs from "utils/UtilsFncs";

import {
  setSelectedProducts,
  updateProductFilters,
} from "redux/reducers/criarVitrineReducer";

import InputText from "components/_refactor/text/input";
export default function IncludeProducts() {
  const [isLoading, setIsLoading] = useState(false);
  const [productSearch, setProductSearch] = useState(null);
  const [preSelectedProducts, setPreSelectedProducts] = useState([]);
  const [productsData, setProductsData] = useState([]);
  const [totalSize, setTotalSize] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [currentSizePerPage, setCurrentSizePerPage] = useState(5);
  const [open, setOpen] = useState(false);

  const [productsTablePagination, setProductsTablePagination] = useState({
    page: 1,
    sizePerPage: 10,
  });

  const dispatch = useDispatch();

  const { selectedProducts, productFilters, formData } = useSelector(
    (state) => state.criarVitrine,
  );
  const productFiltersRef = useRef(productFilters);

  const buildSearchBody = useCallback(
    async ({
      mergedFilters,
      page,
      pageSize,
      filterValue,
      base64FileOverride,
    }) => {
      const eanValue = Array.isArray(mergedFilters?.sku)
        ? mergedFilters.sku.length > 0
          ? mergedFilters.sku.map((item) => item?.value).filter(Boolean)
          : null
        : mergedFilters?.sku?.value
          ? [mergedFilters.sku.value]
          : null;

      const base64File =
        base64FileOverride ??
        (mergedFilters?.importFile
          ? await UtilsFncs.FileToB64(mergedFilters?.importFile)
          : null);

      return {
        page,
        pageSize,
        idMarca: formData?.brand?.value,
        categoria: mergedFilters?.category?.label,
        subcategoria: mergedFilters?.subcategory?.label,
        colecao_produto: mergedFilters?.collection?.label,
        nomeComercial: mergedFilters?.commercialName?.label,
        status: mergedFilters?.status?.label,
        apenasComEstoque: true,
        ean: eanValue?.toString(),
        filter: filterValue,
        base64File,
        fileType: mergedFilters?.importFile?.type,
      };
    },
    [formData?.brand?.value],
  );

  const handleSearch = useCallback(
    async (overrides) => {
      const baseFilters = productFilters;
      const mergedFilters = { ...baseFilters, ...overrides };
      const page = overrides?.page ?? mergedFilters?.page ?? currentPage + 1;
      const sizePerPage =
        overrides?.sizePerPage ??
        mergedFilters?.sizePerPage ??
        currentSizePerPage;
      const filterValue = overrides?.filter ?? productSearch;

      try {
        setIsLoading(true);
        dispatch(
          updateProductFilters({
            ...mergedFilters,
            page,
            sizePerPage,
          }),
        );

        const response = await Api.post(
          "product-showcase-list",
          "product",
          await buildSearchBody({
            mergedFilters,
            page,
            pageSize: sizePerPage,
            filterValue,
          }),
          true,
        );

        setProductsData(response.Result?.ListaProdutos ?? []);
        setTotalSize(response.Result?.TotalRegistros ?? 0);
        setPreSelectedProducts(selectedProducts);
        setOpen(true);
      } catch (error) {
        console.error("Erro ao buscar produtos:", error);
      } finally {
        setIsLoading(false);
      }
    },
    [
      currentPage,
      currentSizePerPage,
      dispatch,
      formData?.brand?.value,
      selectedProducts,
      productSearch,
      productFilters,
    ],
  );

  const handleSelectAllResults = useCallback(async () => {
    const mergedFilters = productFiltersRef.current ?? {};
    const pageSize = 100;
    const filterValue = productSearch ?? null;
    const base64File = mergedFilters?.importFile
      ? await UtilsFncs.FileToB64(mergedFilters?.importFile)
      : null;

    const all = [];
    let total = 0;
    let page = 0;

    const LOAD = true;

    while (LOAD) {
      const response = await Api.post(
        "product-showcase-list",
        "product",
        await buildSearchBody({
          mergedFilters,
          page,
          pageSize,
          filterValue,
          allItens: true,
          base64FileOverride: base64File,
        }),
        true,
      );

      const list = response.Result?.ListaProdutos ?? [];
      total = response.Result?.TotalRegistros ?? total;
      all.push(...list);

      if (all.length >= total || list.length === 0) break;
      page += 1;
      if (page > 10000) break;
    }

    const map = new Map();
    all.forEach((p) => {
      if (p?.Codigo != null) map.set(p.Codigo, p);
    });
    return Array.from(map.values());
  }, [buildSearchBody, productSearch]);

  const debouncedSearch = useMemo(
    () =>
      debounce((filters) => {
        handleSearch(filters);
      }, 300),
    [],
  );

  const handleAddProducts = () => {
    dispatch(
      setSelectedProducts(
        Array.from(new Set([...selectedProducts, ...preSelectedProducts])),
      ),
    );

    setOpen(false);
  };

  const handlePaginationChange = (page, sizePerPage) => {
    const nextSize = sizePerPage ?? currentSizePerPage;
    const nextPage = page ?? currentPage;

    setCurrentSizePerPage(nextSize);
    setCurrentPage(nextPage);

    handleSearch({
      ...productFilters,
      page: nextPage - 1,
      sizePerPage: nextSize,
    });
  };

  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  useEffect(() => {
    productFiltersRef.current = productFilters;
  }, [productFilters]);

  useEffect(() => {
    if (productSearch !== null) {
      debouncedSearch({
        ...productFiltersRef.current,
        page: 1,
        filter: productSearch,
      });
    }
  }, [productSearch, debouncedSearch]);

  return (
    <>
      <Filters
        handleSearch={handleSearch}
        searching={isLoading}
        disableImportFile={selectedProducts.length > 0}
        idMarca={formData?.brand?.value}
      />
      <ModalComponent open={open} onClose={() => setOpen(false)} width="80vw">
        {!isLoading &&
          productsData.length !== 0 &&
          productsData.length < totalSize && (
            <Box style={{ display: "flex", marginBottom: "20px" }}>
              <InputText
                label="Pesquisar produto"
                onChange={(e) => {
                  setProductSearch(e.target.value);
                }}
                value={productSearch}
              />
            </Box>
          )}
        <SelectProductsTable
          data={productsData}
          loading={isLoading}
          totalSize={totalSize}
          currentPage={currentPage}
          currentSizePerPage={currentSizePerPage}
          selectedProducts={preSelectedProducts}
          setSelectedProducts={setPreSelectedProducts}
          handlePaginationChange={handlePaginationChange}
          onSelectAllResults={handleSelectAllResults}
        />
        <Box style={{ display: "flex", justifyContent: "flex-end" }}>
          <Button
            variant="contained"
            color="primary"
            disabled={
              preSelectedProducts.length === 0 && selectedProducts.length === 0
            }
            onClick={handleAddProducts}
          >
            Adicionar
          </Button>
        </Box>
      </ModalComponent>
      <Box mt={6}>
        <ProductsTable
          totalSize={selectedProducts?.length ?? 0}
          currentPage={productsTablePagination.page}
          currentSizePerPage={productsTablePagination.sizePerPage}
          setProductsTablePagination={setProductsTablePagination}
        />
      </Box>
    </>
  );
}
